import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import ProcedureRunner from '@/components/ProcedureRunner';
import RecommendedTestPopup from '@/components/RecommendedTestPopup';
import DiagnosticTabBar from '@/components/DiagnosticTabBar';
import { useSessions } from '@/lib/sessions';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Search, AlertTriangle, CheckCircle2, Gauge, Wrench, Sparkles, BadgeCheck, Target, FlaskConical, FileText, ListChecks, Plus } from 'lucide-react';

const STEPS = [
  { key: 'brand', label: 'Marca' },
  { key: 'model', label: 'Modello' },
  { key: 'symptom', label: 'Codice / Sintomo' },
  { key: 'result', label: 'Risultato' },
];

export default function GuidedDiagnosisPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const { activeGuided, scheduleUpdate, createSession } = useSessions();
  const [step, setStep] = useState(0);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [symptom, setSymptom] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const hydratingRef = useRef(false);

  // Hydrate local state from the active session whenever we switch tab
  useEffect(() => {
    hydratingRef.current = true;
    if (!activeGuided) {
      setStep(0); setBrand(''); setModel(''); setSymptom('');
      setProvider('anthropic'); setResult(null);
    } else {
      setBrand(activeGuided.brand || '');
      setModel(activeGuided.model || '');
      const s = activeGuided.state || {};
      setStep(typeof s.step === 'number' ? s.step : 0);
      setSymptom(s.symptom || '');
      setProvider(s.provider || 'anthropic');
      setResult(s.result || null);
    }
    const tid = setTimeout(() => { hydratingRef.current = false; }, 0);
    return () => clearTimeout(tid);
  }, [activeGuided?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Sync local state back to the active session (debounced inside the provider)
  useEffect(() => {
    if (!activeGuided || hydratingRef.current) return;
    scheduleUpdate(activeGuided.id, {
      brand, model,
      state: { step, symptom, provider, result },
    });
  }, [activeGuided?.id, brand, model, step, symptom, provider, result, scheduleUpdate]);

  useEffect(() => { api.get('/brands').then((r) => setBrands(r.data)); }, []);
  useEffect(() => {
    if (brand) api.get(`/brands/${encodeURIComponent(brand)}/models`).then((r) => setModels(r.data));
  }, [brand]);

  const next = () => {
    if (step === 0 && !brand) return toast.error('Seleziona la marca');
    if (step === 1 && !model) return toast.error('Seleziona il modello');
    if (step === 2 && !symptom.trim()) return toast.error('Inserisci codice errore o sintomo');
    if (step === 2) runDiagnosis(); else setStep(step + 1);
  };

  const prev = () => setStep(Math.max(0, step - 1));
  const reset = () => { setStep(0); setBrand(''); setModel(''); setSymptom(''); setResult(null); };

  const runDiagnosis = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post('/diagnosis/guided', {
        brand, model, error_code_or_symptom: symptom, provider,
      });
      setResult(data);
      setStep(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally { setLoading(false); }
  };

  return (
    <div className="animate-fade-up">
      <DiagnosticTabBar kind="guided" />

      {!activeGuided ? (
        <div className="flex items-center justify-center py-20">
          <div className="surface p-10 text-center max-w-md" data-testid="guided-empty-state">
            <ListChecks className="w-12 h-12 text-gray-300 mx-auto" strokeWidth={1.2} />
            <h2 className="font-display font-bold text-xl mt-4">Diagnosi Guidata</h2>
            <p className="text-sm text-gray-500 mt-2">
              {t('sessions.empty_state_guided') || 'Nessuna sessione attiva. Crea la tua prima sessione per iniziare.'}
            </p>
            <button
              onClick={() => createSession('guided')}
              className="btn-primary mt-5 mx-auto"
              data-testid="guided-create-first"
            >
              <Plus className="w-4 h-4" /> {t('sessions.start_now') || 'Avvia ora'}
            </button>
          </div>
        </div>
      ) : (
        <>
      <div className="mt-4 flex items-baseline gap-3 flex-wrap">
        <div className="overline text-[#002FA7]">WIZARD</div>
        <span className="text-[10px] font-mono uppercase text-green-700" data-testid="guided-autosave-indicator">
          · {t('sessions.autosave') || 'salvataggio automatico'}
        </span>
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight mt-1">{activeGuided.title || 'Diagnosi Guidata'}</h1>
      <p className="text-gray-500 mt-1 text-sm">Procedura passo-passo. Seleziona la macchina, indica il sintomo, ottieni cause e test.</p>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2" data-testid="stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-2 ${i === step ? 'text-[#002FA7]' : i < step ? 'text-green-700' : 'text-gray-400'}`}>
              <div className={`w-7 h-7 flex items-center justify-center text-xs font-mono font-bold border ${
                i === step ? 'bg-[#002FA7] border-[#002FA7] text-white' :
                i < step ? 'bg-green-600 border-green-600 text-white' :
                'bg-white border-gray-300'
              }`}>{i + 1}</div>
              <span className="text-sm font-semibold hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-green-600' : 'bg-gray-200'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="surface p-6 mt-6 min-h-[320px]" data-testid="step-body">
        {step === 0 && (
          <div>
            <div className="overline text-gray-500">STEP 1 / 3</div>
            <h2 className="font-display text-2xl font-bold mt-2">Seleziona la marca</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-6">
              {brands.length === 0 && <div className="col-span-full text-sm text-gray-500">Nessuna marca disponibile. Carica prima dei documenti.</div>}
              {brands.map((b) => (
                <button key={b} onClick={() => setBrand(b)} data-testid={`brand-${b}`}
                  className={`border p-4 text-left transition-all ${brand === b ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-gray-200 hover:border-gray-400'}`}>
                  <div className="font-display font-bold">{b}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 1 && (
          <div>
            <div className="overline text-gray-500">STEP 2 / 3</div>
            <h2 className="font-display text-2xl font-bold mt-2">Seleziona il modello <span className="text-gray-400">/ {brand}</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-6">
              {models.length === 0 && <div className="col-span-full text-sm text-gray-500">Nessun modello per questa marca.</div>}
              {models.map((m) => (
                <button key={m} onClick={() => setModel(m)} data-testid={`model-${m}`}
                  className={`border p-4 text-left transition-all ${model === m ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-gray-200 hover:border-gray-400'}`}>
                  <div className="font-display font-bold">{m}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 2 && (
          <div>
            <div className="overline text-gray-500">STEP 3 / 3</div>
            <h2 className="font-display text-2xl font-bold mt-2">Codice errore o sintomo</h2>
            <p className="text-sm text-gray-500 mt-1">{brand} {model}</p>
            <div className="mt-6">
              <label className="overline text-gray-600 block mb-1">Descrivi il problema</label>
              <textarea rows={4} className="input-base font-mono text-base"
                value={symptom} onChange={(e) => setSymptom(e.target.value)}
                placeholder="es. E42 — Il motore di trazione non parte dopo 3 secondi"
                data-testid="symptom-input" />
            </div>
            <div className="mt-4">
              <label className="overline text-gray-600 block mb-1">Modello AI</label>
              <select value={provider} onChange={(e) => setProvider(e.target.value)} className="input-base" data-testid="guided-provider">
                <option value="anthropic">Claude Sonnet 4.5</option>
                <option value="openai">GPT-5.2</option>
              </select>
            </div>
          </div>
        )}
        {step === 3 && (
          <div data-testid="diagnosis-result">
            {loading ? (
              <div className="text-center py-10 text-gray-500">Analisi in corso…</div>
            ) : result && !result.found ? (
              <div className="border border-yellow-300 bg-yellow-50 p-6 text-center">
                <AlertTriangle className="w-10 h-10 text-yellow-700 mx-auto" strokeWidth={1.5} />
                <div className="font-display font-bold text-lg mt-3">Informazione non trovata</div>
                <div className="text-sm text-gray-700 mt-1">{result.message || 'Informazione non trovata nei documenti caricati.'}</div>
              </div>
            ) : result && (
              <div className="space-y-6">
                <div className="border border-green-300 bg-green-50 p-4 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-700 mt-0.5" />
                  <div>
                    <div className="font-display font-bold">Diagnosi disponibile</div>
                    <div className="text-sm text-gray-700 font-mono mt-1">{brand} / {model} · {symptom}</div>
                  </div>
                </div>

                {(result.matching_patterns || []).length > 0 && (
                  <section className="border-l-4 border-blue-500 bg-blue-50/40 p-4" data-testid="matching-patterns">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-blue-700" />
                      <h3 className="font-display text-xl font-bold text-blue-900">Procedure validate sul campo</h3>
                      <span className="ml-1 text-xs text-blue-700 font-mono">({result.matching_patterns.length})</span>
                    </div>
                    <p className="text-xs text-blue-800 mb-3">Pattern già verificati da tecnici per casi simili. Usa questi prima delle ipotesi AI sotto.</p>
                    <ul className="space-y-2">
                      {result.matching_patterns.map((p) => {
                        const symLabel = (p.symptom && (p.symptom.it || p.symptom.es || p.symptom.en)) || '';
                        const causeLabel = (p.probable_cause && (p.probable_cause.it || p.probable_cause.es || p.probable_cause.en)) || '';
                        return (
                          <li key={p.id} className="bg-white border border-blue-200 p-3" data-testid={`matching-pattern-${p.id}`}>
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              {p.error_code && <span className="bg-red-100 text-red-800 px-2 py-0.5 text-xs font-mono font-bold">{p.error_code}</span>}
                              {p.validated_by_name && (
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-mono">
                                  <BadgeCheck className="w-3 h-3" /> validato
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 text-xs font-mono">
                                <Target className="w-3 h-3" /> {p.success_count || 0} successi
                              </span>
                              <span className="text-xs text-gray-500 font-mono">conf {p.confidence}/5</span>
                            </div>
                            <div className="font-display font-bold text-sm">{symLabel}</div>
                            {causeLabel && <div className="text-xs text-gray-600 mt-1">→ {causeLabel}</div>}
                            {(p.procedure_steps || []).length > 0 && (
                              <ol className="mt-2 space-y-1.5">
                                {p.procedure_steps.map((s) => {
                                  const action = (s && (s.it || s.es || s.en)) || '';
                                  return (
                                    <li key={s.order} className="flex items-start gap-2 text-xs">
                                      <span className="w-5 h-5 bg-blue-700 text-white flex items-center justify-center text-[10px] font-mono font-bold shrink-0">{s.order}</span>
                                      <div>
                                        <div className="text-gray-900">{action}</div>
                                        {s.expected_value && <div className="text-[0.65rem] text-gray-500 font-mono">attesa: {s.expected_value}</div>}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                {(result.cross_check?.controls || []).length > 0 && (
                  <section className="border-l-4 border-amber-500 bg-amber-50/40 p-4" data-testid="cross-check-list">
                    <div className="flex items-center gap-2 mb-3">
                      <FlaskConical className="w-4 h-4 text-amber-700" />
                      <h3 className="font-display text-xl font-bold text-amber-900">Lista controlli prioritizzata</h3>
                      <span className="ml-1 text-xs text-amber-700 font-mono">
                        ({result.cross_check.controls.length})
                      </span>
                      {result.cross_check.has_resolved_field_report && (
                        <span className="ml-auto inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 text-[10px] font-mono uppercase">
                          <CheckCircle2 className="w-3 h-3" /> Caso reale risolto trovato
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-amber-900 mb-3">
                      Cross-check fra <strong>Field Reports</strong> (casi reali), <strong>Procedure Patterns</strong> (procedure validate) e <strong>Liste Allarmi</strong> caricate. Ordinati per probabilità di risoluzione.
                    </p>
                    <ol className="space-y-2">
                      {result.cross_check.controls.map((c, idx) => {
                        const isFR = c.source === 'field_report';
                        const isPP = c.source === 'procedure_pattern';
                        const isAD = c.source === 'alarm_doc';
                        const sourceLabel = isFR ? 'FIELD REPORT' : isPP ? 'PROCEDURE PATTERN' : 'LISTA ALLARMI';
                        const sourceClass = isFR
                          ? 'bg-green-100 text-green-900 border-green-300'
                          : isPP ? 'bg-blue-100 text-blue-900 border-blue-300'
                            : 'bg-gray-100 text-gray-800 border-gray-300';
                        const Icon = isFR ? BadgeCheck : isPP ? Sparkles : FileText;
                        return (
                          <li key={`${c.source}-${c.id || idx}`} className="bg-white border border-amber-200 p-3" data-testid={`control-${c.source}-${idx}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-7 h-7 bg-amber-600 text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center flex-wrap gap-2 mb-1">
                                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 border ${sourceClass} inline-flex items-center gap-1`}>
                                    <Icon className="w-3 h-3" /> {sourceLabel}
                                  </span>
                                  {c.error_code && <span className="bg-red-100 text-red-800 px-2 py-0.5 text-xs font-mono font-bold">{c.error_code}</span>}
                                  {isFR && c.result === 'risolto' && (
                                    <span className="inline-flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 text-[10px] font-mono uppercase">
                                      <CheckCircle2 className="w-3 h-3" /> risolto
                                    </span>
                                  )}
                                  {isPP && c.validated_by_name && (
                                    <span className="inline-flex items-center gap-1 bg-blue-600 text-white px-2 py-0.5 text-[10px] font-mono uppercase">
                                      validato
                                    </span>
                                  )}
                                  {isPP && c.success_count > 0 && (
                                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 text-xs font-mono">
                                      <Target className="w-3 h-3" /> {c.success_count}
                                    </span>
                                  )}
                                  {isAD && c.filename && (
                                    <span className="text-xs text-gray-500 font-mono truncate">{c.filename}{c.page ? ` · p.${c.page}` : ''}</span>
                                  )}
                                </div>
                                <div className="font-display font-bold text-sm">{c.title || '—'}</div>
                                {c.root_cause && (
                                  <div className="text-xs text-gray-700 mt-1">
                                    <span className="overline text-red-700 mr-1">causa:</span>{c.root_cause}
                                  </div>
                                )}
                                {c.steps && (
                                  <div className="mt-2 text-xs text-gray-800 whitespace-pre-wrap bg-gray-50 border border-gray-200 p-2 font-mono leading-relaxed">
                                    {c.steps}
                                  </div>
                                )}
                                {c.parts_replaced && (
                                  <div className="text-[0.7rem] text-gray-500 mt-1">
                                    <span className="overline text-gray-600 mr-1">parti sostituite:</span>{c.parts_replaced}
                                  </div>
                                )}
                                {(c.technician_name || c.validated_by_name) && (
                                  <div className="text-[0.65rem] text-gray-500 mt-1 font-mono">
                                    {c.technician_name && <>👤 {c.technician_name}</>}
                                    {c.validated_by_name && <> · validato da {c.validated_by_name}</>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </section>
                )}

                {(result.universal_procedures || []).length > 0 && (
                  <section className="border-l-4 border-indigo-500 bg-indigo-50/40 p-4" data-testid="universal-procedures-list">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <FlaskConical className="w-4 h-4 text-indigo-700" />
                      <h3 className="font-display text-xl font-bold text-indigo-900">Procedure di test universali</h3>
                      <span className="text-xs text-indigo-700 font-mono">({result.universal_procedures.length})</span>
                      {(result.universal_categories || []).length > 0 && (
                        <span className="text-[10px] text-indigo-700 font-mono uppercase ml-1">
                          [{result.universal_categories.join(' · ')}]
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-indigo-800 mb-3">Recuperate automaticamente in base alla categoria rilevata. Applicabili a tutti i produttori.</p>
                    <ul className="space-y-2">
                      {result.universal_procedures.map((p) => {
                        const title = (p.title || {})[lang] || (p.title || {}).it || (p.title || {}).en || p.category;
                        const desc = (p.description || {})[lang] || '';
                        const safety = (p.safety_warnings || {})[lang] || '';
                        const tools = (p.required_tools || {})[lang] || '';
                        return (
                          <li key={p.id} className="bg-white border border-indigo-200 p-3" data-testid={`up-${p.category}`}>
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="bg-indigo-100 text-indigo-900 px-2 py-0.5 text-[10px] font-mono uppercase">{p.category}</span>
                              <span className="font-display font-bold text-sm">{title}</span>
                            </div>
                            {desc && <div className="text-xs text-gray-700">{desc}</div>}
                            {safety && (
                              <div className="mt-2 border-l-2 border-red-400 bg-red-50/40 pl-2 py-1 text-[11px] text-red-900">
                                ⚠ {safety}
                              </div>
                            )}
                            {tools && (
                              <div className="mt-2 text-[11px] text-gray-600"><span className="overline text-gray-500">strumenti:</span> {tools}</div>
                            )}
                            <div className="mt-2">
                              <ProcedureRunner
                                procedure={p}
                                lang={lang}
                                context={{ brand, model, question: symptom }}
                                onCompleted={runDiagnosis}
                              />
                            </div>
                            {(p.steps || []).length > 0 && (
                              <ol className="mt-2 space-y-1">
                                {p.steps.map((s) => {
                                  const action = s[lang] || s.it || s.en || '';
                                  return (
                                    <li key={s.order} className="flex items-start gap-2 text-xs">
                                      <span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-mono font-bold shrink-0">{s.order}</span>
                                      <div className="flex-1">
                                        <div className="text-gray-900">{action}</div>
                                        {s.expected_value && <div className="text-[10px] text-green-700 font-mono">→ {s.expected_value}</div>}
                                        {s.tool && <div className="text-[9px] text-gray-500 font-mono">tool: {s.tool}</div>}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                )}

                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <h3 className="font-display text-xl font-bold">Possibili cause</h3>
                  </div>
                  <ul className="surface divide-y divide-gray-200">
                    {(result.possibili_cause || []).map((c, i) => (
                      <li key={`cause-${i}-${(c || '').slice(0, 24)}`} className="p-3 flex gap-3 text-sm">
                        <span className="font-mono text-xs text-red-700 font-bold">{String(i + 1).padStart(2, '0')}</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Wrench className="w-4 h-4 text-[#002FA7]" />
                    <h3 className="font-display text-xl font-bold">Test passo-passo</h3>
                  </div>
                  <ol className="surface divide-y divide-gray-200">
                    {(result.test_passo_passo || []).map((t, i) => (
                      <li key={`test-${t.numero || i}-${(t.azione || '').slice(0, 24)}`} className="p-3 text-sm">
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-[#002FA7] text-white flex items-center justify-center text-xs font-mono font-bold shrink-0">{t.numero || i + 1}</span>
                          <div>
                            <div className="font-semibold">{t.azione}</div>
                            {t.strumento && <div className="text-xs text-gray-500 mt-0.5 font-mono">Strumento: {t.strumento}</div>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>

                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Gauge className="w-4 h-4 text-green-700" />
                    <h3 className="font-display text-xl font-bold">Valori attesi</h3>
                  </div>
                  <div className="surface overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="p-3 overline text-gray-600">Parametro</th>
                          <th className="p-3 overline text-gray-600">Valore</th>
                          <th className="p-3 overline text-gray-600">Unità</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 font-mono">
                        {(result.valori_attesi || []).map((v, i) => (
                          <tr key={`val-${i}-${(v.parametro || '').slice(0, 24)}`}>
                            <td className="p-3">{v.parametro}</td>
                            <td className="p-3 font-bold">{v.valore}</td>
                            <td className="p-3 text-gray-600">{v.unita}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-between">
        <button onClick={step === 3 ? reset : prev} className="btn-secondary" data-testid="wizard-prev">
          {step === 3 ? 'Nuova diagnosi' : <><ChevronLeft className="w-4 h-4" /> Indietro</>}
        </button>
        {step < 3 && (
          <button onClick={next} className="btn-primary" data-testid="wizard-next" disabled={loading}>
            {step === 2 ? (<><Search className="w-4 h-4" /> {loading ? 'Analisi…' : 'Avvia diagnosi'}</>) : (<>Avanti <ChevronRight className="w-4 h-4" /></>)}
          </button>
        )}
      </div>

      {/* Recommended universal test pop-up: opens automatically when the AI returns one or
          more universal procedures, so the technician sees the suggestion prominently
          without scrolling. Dismissed-per-response via signature. */}
      {step === 3 && result && (
        <RecommendedTestPopup
          procedures={result.universal_procedures || []}
          context={{ brand, model, question: symptom }}
          lang={lang}
          onRunnerCompleted={runDiagnosis}
        />
      )}
        </>
      )}
    </div>
  );
}
